import React, { useMemo, useState } from 'react';
interface GlossaryTerm {
  id: string;
  english: string;
  chinese: string;
  pinyin?: string;
  category: string;
  explanation: string;
  example?: string;
  tags?: string[];
  badge?: {
    type: 'chip' | 'mult' | 'xmult' | 'gold' | 'purple' | 'green' | 'red';
    text: string;
  };
}
const categories = [{
  id: 'all',
  label: '全部',
  emoji: '📚'
}, {
  id: 'basics',
  label: '基础概念',
  emoji: '🎮'
}, {
  id: 'scoring',
  label: '计分系统',
  emoji: '💯'
}, {
  id: 'hands',
  label: '牌型',
  emoji: '🃏'
}, {
  id: 'cards',
  label: '扑克牌',
  emoji: '♠️'
}, {
  id: 'jokers',
  label: '小丑牌',
  emoji: '🤡'
}, {
  id: 'consumables',
  label: '消耗品',
  emoji: '✨'
}, {
  id: 'shop',
  label: '商店',
  emoji: '🛒'
}, {
  id: 'special',
  label: '特殊牌',
  emoji: '⭐'
}, {
  id: 'decks',
  label: '牌组',
  emoji: '🎴'
}];
const terms: GlossaryTerm[] = [
// BASICS
{
  id: 'blind',
  english: 'Blind',
  chinese: '盲注',
  pinyin: 'máng zhù',
  category: 'basics',
  explanation: '每一关需要达到的目标分数。你必须在规定的出牌次数内，让得分超过盲注才能过关。每组有三个盲注：小盲注→大盲注→Boss盲注。',
  example: '第一关小盲注通常是300分，你需要打出足够高分的牌型来超过它。',
  tags: ['核心概念']
}, {
  id: 'small-blind',
  english: 'Small Blind',
  chinese: '小盲注',
  pinyin: 'xiǎo máng zhù',
  category: 'basics',
  explanation: '每组三关中的第一关，目标分数最低，最容易通过。通过后获得金币奖励。',
  tags: ['盲注类型']
}, {
  id: 'big-blind',
  english: 'Big Blind',
  chinese: '大盲注',
  pinyin: 'dà máng zhù',
  category: 'basics',
  explanation: '每组三关中的第二关，目标分数比小盲注高约50%。通过后获得更多金币。',
  tags: ['盲注类型']
}, {
  id: 'boss-blind',
  english: 'Boss Blind',
  chinese: 'Boss盲注',
  pinyin: 'Boss máng zhù',
  category: 'basics',
  explanation: '每组三关中的最后一关，最难！不仅分数目标最高，还有特殊的负面效果（比如某些花色的牌被翻面、手牌减少等）。通过后进入商店，可以购买小丑牌。',
  example: '某个Boss盲注可能让所有红心牌变成石头牌，大幅降低你的得分。',
  tags: ['盲注类型', '困难'],
  badge: {
    type: 'red',
    text: '⚠️ 特殊效果'
  }
}, {
  id: 'ante',
  english: 'Ante',
  chinese: '底注 / 关卡层数',
  pinyin: 'dǐ zhù',
  category: 'basics',
  explanation: '游戏的进度层数，每完成一组三个盲注就进入下一个Ante。Ante越高，盲注分数越高，难度越大。标准游戏共8个Ante。',
  example: 'Ante 1的小盲注是300分，Ante 4可能就需要几万分了。',
  tags: ['核心概念']
}, {
  id: 'run',
  english: 'Run',
  chinese: '一局游戏',
  pinyin: 'yī jú yóuxì',
  category: 'basics',
  explanation: '从开始到结束（通关或失败）的完整一次游戏。每次Run都是随机的，小丑牌、牌序都不同。',
  tags: ['核心概念']
}, {
  id: 'hand',
  english: 'Hand',
  chinese: '手牌 / 出牌次数',
  pinyin: 'shǒu pái',
  category: 'basics',
  explanation: '有两个含义：①你持有的牌（Hand of cards）；②每轮可以出牌的次数（Hands remaining）。每轮默认有4次出牌机会，用完未达标就失败。',
  example: '"You have 3 hands left" = 你还有3次出牌机会',
  tags: ['核心概念']
}, {
  id: 'discard',
  english: 'Discard',
  chinese: '弃牌',
  pinyin: 'qì pái',
  category: 'basics',
  explanation: '不打出牌，而是把选中的牌丢弃，然后从牌堆补充新牌。每轮默认有3次弃牌机会。弃牌不计分，用来换取更好的牌。',
  example: '手里没有好牌型时，可以弃掉差牌，希望摸到更好的牌。',
  tags: ['核心概念']
}, {
  id: 'stake',
  english: 'Stake',
  chinese: '难度等级',
  pinyin: 'nán dù děng jí',
  category: 'basics',
  explanation: '游戏的难度设置，从白色（最简单）到金色（最难）共8个等级。更高的Stake会增加各种限制，但通关奖励也更丰厚。',
  tags: ['难度']
}, {
  id: 'seed',
  english: 'Seed',
  chinese: '种子',
  pinyin: 'zhǒngzǐ',
  category: 'basics',
  explanation: '决定游戏随机内容的代码。输入相同的Seed会生成完全相同的游戏（相同的小丑牌出现顺序、相同的牌序等）。可以用来分享有趣的游戏或练习特定配置。',
  example: '在主菜单输入别人分享的Seed，就能体验和他们完全一样的游戏。',
  tags: ['进阶']
}, {
  id: 'skip',
  english: 'Skip',
  chinese: '跳过',
  pinyin: 'tiào guò',
  category: 'basics',
  explanation: '在选择盲注时，可以选择跳过小盲注或大盲注，直接进入下一个。跳过会获得标签奖励（Tags），但放弃了金币收入。',
  tags: ['策略']
}, {
  id: 'tag',
  english: 'Tag',
  chinese: '标签',
  pinyin: 'biāo qiān',
  category: 'basics',
  explanation: '跳过盲注时获得的奖励。不同标签有不同效果，比如免费获得一张小丑牌、商店折扣、额外的手牌次数等。',
  tags: ['奖励'],
  badge: {
    type: 'gold',
    text: '🏷️ 跳过奖励'
  }
},
// SCORING
{
  id: 'chips',
  english: 'Chips',
  chinese: '筹码',
  pinyin: 'chóu mǎ',
  category: 'scoring',
  explanation: '计分公式的第一部分。来源：①牌型基础筹码值；②打出的牌的点数值（2=2筹码，A=11筹码）；③小丑牌/增强效果。',
  example: '打出一对K：基础10筹码 + K(10) + K(10) = 30筹码',
  badge: {
    type: 'chip',
    text: '蓝色数字'
  },
  tags: ['核心计分']
}, {
  id: 'mult',
  english: 'Mult / Multiplier',
  chinese: '倍率',
  pinyin: 'bèi lǜ',
  category: 'scoring',
  explanation: '计分公式的第二部分，与筹码相乘得到最终分数。来源：①牌型基础倍率；②小丑牌+Mult效果（加法）；③小丑牌×Mult效果（乘法，更强！）。',
  example: '30筹码 × 6倍率 = 180分',
  badge: {
    type: 'mult',
    text: '红色数字'
  },
  tags: ['核心计分']
}, {
  id: 'plus-chips',
  english: '+Chips',
  chinese: '+筹码',
  pinyin: 'jiā chóu mǎ',
  category: 'scoring',
  explanation: '小丑牌或增强效果给筹码加上固定数值。例如"+50 Chips"表示每次触发时筹码增加50。加法效果，对提升基数有帮助。',
  badge: {
    type: 'chip',
    text: '+Chips'
  },
  tags: ['效果类型']
}, {
  id: 'plus-mult',
  english: '+Mult',
  chinese: '+倍率',
  pinyin: 'jiā bèi lǜ',
  category: 'scoring',
  explanation: '小丑牌或增强效果给倍率加上固定数值。例如"+4 Mult"表示倍率增加4。加法效果，多个+Mult小丑牌叠加时效果显著。',
  badge: {
    type: 'mult',
    text: '+Mult'
  },
  tags: ['效果类型']
}, {
  id: 'xmult',
  english: '×Mult',
  chinese: '×倍率（乘法倍率）',
  pinyin: 'chéng bèi lǜ',
  category: 'scoring',
  explanation: '最强的倍率类型！将当前倍率乘以一个系数。例如"×1.5 Mult"表示当前倍率乘以1.5。乘法效果比加法强得多，是高分配置的核心。',
  example: '当前倍率是10，遇到×1.5效果 → 变成15倍率（而不是11.5）',
  badge: {
    type: 'xmult',
    text: '×Mult 最强！'
  },
  tags: ['效果类型', '重要']
}, {
  id: 'trigger',
  english: 'Trigger',
  chinese: '触发',
  pinyin: 'chù fā',
  category: 'scoring',
  explanation: '小丑牌或特殊效果被激活的时机。例如"Triggers when you play a Flush"意思是"当你打出同花时触发"。理解触发条件是使用小丑牌的关键。',
  tags: ['机制']
}, {
  id: 'retrigger',
  english: 'Retrigger',
  chinese: '重复触发',
  pinyin: 'chóng fù chù fā',
  category: 'scoring',
  explanation: '让一张牌的计分效果再次触发。例如某张牌原本给+10筹码，被重复触发后再给一次+10筹码。重复触发可以大幅提升得分。',
  tags: ['机制', '进阶'],
  badge: {
    type: 'gold',
    text: '💫 双倍计分'
  }
}, {
  id: 'scoring-cards',
  english: 'Scoring Cards',
  chinese: '计分牌',
  pinyin: 'jì fēn pái',
  category: 'scoring',
  explanation: '打出的5张牌中，实际参与计分的牌。例如打出一对K，只有两张K是计分牌，其余3张不贡献筹码（但可能触发其他效果）。',
  tags: ['机制']
},
// HAND TYPES
{
  id: 'high-card',
  english: 'High Card',
  chinese: '高牌',
  pinyin: 'gāo pái',
  category: 'hands',
  explanation: '最弱的牌型。5张牌中没有任何组合，只有最大的那张牌算数。基础：5筹码 × 1倍率。',
  badge: {
    type: 'chip',
    text: '5×1'
  },
  tags: ['牌型']
}, {
  id: 'one-pair',
  english: 'One Pair',
  chinese: '一对',
  pinyin: 'yī duì',
  category: 'hands',
  explanation: '5张牌中有2张点数相同。基础：10筹码 × 2倍率。',
  badge: {
    type: 'chip',
    text: '10×2'
  },
  tags: ['牌型']
}, {
  id: 'two-pair',
  english: 'Two Pair',
  chinese: '两对',
  pinyin: 'liǎng duì',
  category: 'hands',
  explanation: '5张牌中有两组不同的对子。基础：20筹码 × 2倍率。',
  badge: {
    type: 'chip',
    text: '20×2'
  },
  tags: ['牌型']
}, {
  id: 'three-of-a-kind',
  english: 'Three of a Kind',
  chinese: '三条',
  pinyin: 'sān tiáo',
  category: 'hands',
  explanation: '5张牌中有3张点数相同。基础：30筹码 × 3倍率。',
  badge: {
    type: 'chip',
    text: '30×3'
  },
  tags: ['牌型']
}, {
  id: 'straight',
  english: 'Straight',
  chinese: '顺子',
  pinyin: 'shùn zi',
  category: 'hands',
  explanation: '5张牌点数连续（如5-6-7-8-9），花色可以不同。A可以作为最大（A-K-Q-J-10）或最小（A-2-3-4-5）。基础：30筹码 × 4倍率。',
  badge: {
    type: 'chip',
    text: '30×4'
  },
  tags: ['牌型']
}, {
  id: 'flush',
  english: 'Flush',
  chinese: '同花',
  pinyin: 'tóng huā',
  category: 'hands',
  explanation: '5张牌花色全部相同（如全是♥），点数不需要连续。基础：35筹码 × 4倍率。',
  badge: {
    type: 'chip',
    text: '35×4'
  },
  tags: ['牌型']
}, {
  id: 'full-house',
  english: 'Full House',
  chinese: '葫芦',
  pinyin: 'hú lu',
  category: 'hands',
  explanation: '三条 + 一对的组合（如三张K + 两张9）。基础：40筹码 × 4倍率。',
  badge: {
    type: 'chip',
    text: '40×4'
  },
  tags: ['牌型']
}, {
  id: 'four-of-a-kind',
  english: 'Four of a Kind',
  chinese: '四条',
  pinyin: 'sì tiáo',
  category: 'hands',
  explanation: '4张点数相同的牌。基础：60筹码 × 7倍率。非常强力的牌型！',
  badge: {
    type: 'mult',
    text: '60×7'
  },
  tags: ['牌型']
}, {
  id: 'straight-flush',
  english: 'Straight Flush',
  chinese: '同花顺',
  pinyin: 'tóng huā shùn',
  category: 'hands',
  explanation: '既是顺子又是同花——5张点数连续且花色相同。基础：100筹码 × 8倍率。',
  badge: {
    type: 'xmult',
    text: '100×8'
  },
  tags: ['牌型', '强力']
}, {
  id: 'royal-flush',
  english: 'Royal Flush',
  chinese: '皇家同花顺',
  pinyin: 'huáng jiā tóng huā shùn',
  category: 'hands',
  explanation: '最高的同花顺：A-K-Q-J-10且同花色。基础：100筹码 × 8倍率。传统扑克中的最强牌型。',
  badge: {
    type: 'xmult',
    text: '100×8 👑'
  },
  tags: ['牌型', '强力']
}, {
  id: 'five-of-a-kind',
  english: 'Five of a Kind',
  chinese: '五条',
  pinyin: 'wǔ tiáo',
  category: 'hands',
  explanation: '【小丑牌专属】5张点数完全相同的牌，需要特殊小丑牌效果才能实现。基础：120筹码 × 12倍率。',
  badge: {
    type: 'xmult',
    text: '120×12 ⭐'
  },
  tags: ['牌型', '小丑牌专属']
},
// PLAYING CARDS
{
  id: 'rank',
  english: 'Rank',
  chinese: '点数',
  pinyin: 'diǎn shù',
  category: 'cards',
  explanation: '牌面上的数字或字母，表示牌的大小：2, 3, 4, 5, 6, 7, 8, 9, 10, J(Jack), Q(Queen), K(King), A(Ace)。点数决定牌的筹码贡献值。',
  tags: ['基础']
}, {
  id: 'suit',
  english: 'Suit',
  chinese: '花色',
  pinyin: 'huā sè',
  category: 'cards',
  explanation: '牌的符号类型，共四种：♠黑桃(Spades)、♥红心(Hearts)、♦方块(Diamonds)、♣梅花(Clubs)。花色影响同花牌型和部分小丑牌的触发条件。',
  tags: ['基础']
}, {
  id: 'face-card',
  english: 'Face Card',
  chinese: '人头牌',
  pinyin: 'rén tóu pái',
  category: 'cards',
  explanation: '指J（杰克）、Q（皇后）、K（国王）三种牌。许多小丑牌对人头牌有特殊效果。筹码值均为10。',
  tags: ['牌类型']
}, {
  id: 'number-card',
  english: 'Number Card',
  chinese: '数字牌',
  pinyin: 'shù zì pái',
  category: 'cards',
  explanation: '指2到10的牌。筹码值等于牌面数字（2=2筹码，9=9筹码，10=10筹码）。',
  tags: ['牌类型']
}, {
  id: 'ace',
  english: 'Ace',
  chinese: '王牌 / A',
  pinyin: 'wáng pái',
  category: 'cards',
  explanation: '最特殊的牌！筹码值为11（最高）。在顺子中可以作为最大（A-K-Q-J-10）或最小（A-2-3-4-5）。许多小丑牌对A有特殊加成。',
  tags: ['牌类型', '重要'],
  badge: {
    type: 'gold',
    text: '11筹码'
  }
}, {
  id: 'deck',
  english: 'Deck',
  chinese: '牌堆 / 牌组',
  pinyin: 'pái duī',
  category: 'cards',
  explanation: '你的完整牌组，游戏开始时通常是标准的52张牌。游戏过程中可以通过购买、增强等方式修改牌组。',
  tags: ['基础']
},
// JOKERS
{
  id: 'joker',
  english: 'Joker',
  chinese: '小丑牌',
  pinyin: 'xiǎo chǒu pái',
  category: 'jokers',
  explanation: '游戏的核心！特殊的增益卡，放在小丑槽位中（不打出）。每局最多持有5张。小丑牌提供各种强力效果，是高分的关键。',
  badge: {
    type: 'purple',
    text: '🃏 核心机制'
  },
  tags: ['核心']
}, {
  id: 'joker-slot',
  english: 'Joker Slot',
  chinese: '小丑槽位',
  pinyin: 'xiǎo chǒu cáo wèi',
  category: 'jokers',
  explanation: '放置小丑牌的位置，默认最多5个槽位。某些小丑牌或优惠券可以增加槽位数量。',
  tags: ['机制']
}, {
  id: 'rarity',
  english: 'Rarity',
  chinese: '稀有度',
  pinyin: 'xī yǒu dù',
  category: 'jokers',
  explanation: '小丑牌的稀有程度，分四级：Common（普通）、Uncommon（不常见）、Rare（稀有）、Legendary（传说）。稀有度越高，效果通常越强，出现概率越低。',
  tags: ['分类']
}, {
  id: 'common',
  english: 'Common',
  chinese: '普通',
  pinyin: 'pǔ tōng',
  category: 'jokers',
  explanation: '最常见的小丑牌稀有度，灰色边框。效果相对基础，但数量多，容易获得。',
  tags: ['稀有度']
}, {
  id: 'uncommon',
  english: 'Uncommon',
  chinese: '不常见',
  pinyin: 'bù cháng jiàn',
  category: 'jokers',
  explanation: '第二级稀有度，绿色边框。效果比普通更强或更特殊。',
  tags: ['稀有度']
}, {
  id: 'rare',
  english: 'Rare',
  chinese: '稀有',
  pinyin: 'xī yǒu',
  category: 'jokers',
  explanation: '第三级稀有度，蓝色边框。效果强力，出现概率较低。',
  tags: ['稀有度']
}, {
  id: 'legendary',
  english: 'Legendary',
  chinese: '传说',
  pinyin: 'chuán shuō',
  category: 'jokers',
  explanation: '最高稀有度，金色边框。效果极其强力且独特，每种传说小丑在一局中只会出现一次。',
  badge: {
    type: 'gold',
    text: '👑 最高稀有'
  },
  tags: ['稀有度']
}, {
  id: 'edition',
  english: 'Edition',
  chinese: '版本 / 特殊版',
  pinyin: 'bǎn běn',
  category: 'jokers',
  explanation: '小丑牌或扑克牌的特殊版本，提供额外加成。分四种：Foil（闪光）、Holographic（全息）、Polychrome（多彩）、Negative（负片）。',
  tags: ['增强']
}, {
  id: 'foil',
  english: 'Foil',
  chinese: '闪光版',
  pinyin: 'shǎn guāng bǎn',
  category: 'jokers',
  explanation: '特殊版本之一，银色闪光效果。小丑牌获得+50筹码；扑克牌获得+50筹码。',
  badge: {
    type: 'chip',
    text: '+50 Chips'
  },
  tags: ['版本']
}, {
  id: 'holographic',
  english: 'Holographic',
  chinese: '全息版',
  pinyin: 'quán xī bǎn',
  category: 'jokers',
  explanation: '特殊版本之一，彩虹全息效果。小丑牌获得+10倍率；扑克牌获得+10倍率。',
  badge: {
    type: 'mult',
    text: '+10 Mult'
  },
  tags: ['版本']
}, {
  id: 'polychrome',
  english: 'Polychrome',
  chinese: '多彩版',
  pinyin: 'duō cǎi bǎn',
  category: 'jokers',
  explanation: '特殊版本之一，彩色流动效果。小丑牌获得×1.5倍率；扑克牌获得×1.5倍率。乘法效果，非常强力！',
  badge: {
    type: 'xmult',
    text: '×1.5 Mult'
  },
  tags: ['版本', '强力']
}, {
  id: 'negative',
  english: 'Negative',
  chinese: '负片版',
  pinyin: 'fù piàn bǎn',
  category: 'jokers',
  explanation: '特殊版本之一，黑白反色效果。小丑牌获得+1个小丑槽位，相当于可以多持有一张小丑牌！',
  badge: {
    type: 'purple',
    text: '+1 Joker Slot'
  },
  tags: ['版本', '强力']
}, {
  id: 'eternal',
  english: 'Eternal',
  chinese: '永久',
  pinyin: 'yǒng jiǔ',
  category: 'jokers',
  explanation: '小丑牌的特殊属性，表示这张小丑牌无法被出售或销毁。虽然不能卖掉换钱，但某些强力小丑牌获得永久属性后更有保障。',
  tags: ['属性']
}, {
  id: 'perishable',
  english: 'Perishable',
  chinese: '易腐',
  pinyin: 'yì fǔ',
  category: 'jokers',
  explanation: '小丑牌的特殊属性，表示这张小丑牌只能使用有限次数（通常是3个盲注）。用完后自动消失。',
  tags: ['属性']
}, {
  id: 'rental',
  english: 'Rental',
  chinese: '租赁',
  pinyin: 'zū lìn',
  category: 'jokers',
  explanation: '小丑牌的特殊属性，表示每轮结束时需要支付租金（通常是$1）。如果付不起租金，小丑牌会被收走。',
  tags: ['属性']
},
// CONSUMABLES
{
  id: 'tarot',
  english: 'Tarot Card',
  chinese: '塔罗牌',
  pinyin: 'tǎ luó pái',
  category: 'consumables',
  explanation: '消耗品之一，使用后产生一次性效果，然后消失。效果多样：增强扑克牌、改变花色、复制牌等。以塔罗牌名称命名（如The Fool、The Magician等）。',
  tags: ['消耗品']
}, {
  id: 'planet',
  english: 'Planet Card',
  chinese: '星球牌',
  pinyin: 'xīng qiú pái',
  category: 'consumables',
  explanation: '消耗品之一，用于升级特定牌型的基础筹码和倍率。每种星球对应一种牌型（如Jupiter升级Full House，Mars升级Straight等）。',
  example: '使用Jupiter（木星）→ Full House（葫芦）升一级，基础筹码和倍率提升',
  tags: ['消耗品'],
  badge: {
    type: 'chip',
    text: '升级牌型'
  }
}, {
  id: 'spectral',
  english: 'Spectral Card',
  chinese: '幽灵牌',
  pinyin: 'yōu líng pái',
  category: 'consumables',
  explanation: '消耗品之一，效果最为强力和特殊，但也可能有风险。可以复制小丑牌、添加特殊版本、改变牌的属性等。',
  tags: ['消耗品', '强力'],
  badge: {
    type: 'purple',
    text: '⚠️ 高风险高回报'
  }
}, {
  id: 'voucher',
  english: 'Voucher',
  chinese: '优惠券',
  pinyin: 'yōu huì quàn',
  category: 'consumables',
  explanation: '在商店购买的永久升级，效果持续整局游戏。例如：增加手牌数量、增加弃牌次数、商店折扣等。每种优惠券只能购买一次。',
  tags: ['永久升级'],
  badge: {
    type: 'gold',
    text: '永久效果'
  }
},
// SHOP
{
  id: 'shop',
  english: 'Shop',
  chinese: '商店',
  pinyin: 'shāng diàn',
  category: 'shop',
  explanation: '每通过一个Boss盲注后进入的购买界面。可以购买小丑牌、消耗品、优惠券等。商店内容每次都是随机的。',
  tags: ['核心']
}, {
  id: 'reroll',
  english: 'Reroll',
  chinese: '重掷 / 刷新',
  pinyin: 'chóng zhì',
  category: 'shop',
  explanation: '花费金币刷新商店内容，获得新的随机商品。第一次重掷通常花费$5，之后每次递增。',
  example: '商店里没有想要的小丑牌？花$5重掷，看看有没有更好的！',
  tags: ['商店操作']
}, {
  id: 'interest',
  english: 'Interest',
  chinese: '利息',
  pinyin: 'lì xī',
  category: 'shop',
  explanation: '每轮结束时，根据持有的金币获得额外收入。每持有$5获得$1利息，最多$5利息（即持有$25以上时）。鼓励存钱策略。',
  example: '持有$20金币 → 获得$4利息',
  badge: {
    type: 'gold',
    text: '💰 存钱有利息'
  },
  tags: ['金币机制']
}, {
  id: 'sell',
  english: 'Sell',
  chinese: '出售',
  pinyin: 'chū shòu',
  category: 'shop',
  explanation: '将持有的小丑牌或消耗品卖掉换取金币。出售价格通常是购买价格的一半。当小丑牌不再适合当前策略时可以出售。',
  tags: ['商店操作']
},
// SPECIAL CARDS
{
  id: 'enhanced-card',
  english: 'Enhanced Card',
  chinese: '增强牌',
  pinyin: 'zēng qiáng pái',
  category: 'special',
  explanation: '经过特殊增强的扑克牌，有额外效果。增强类型包括：Bonus、Mult、Wild、Glass、Steel、Stone、Gold、Lucky等。',
  tags: ['牌类型']
}, {
  id: 'bonus-card',
  english: 'Bonus Card',
  chinese: '奖励牌',
  pinyin: 'jiǎng lì pái',
  category: 'special',
  explanation: '增强牌之一，橙色背景。每次计分时额外给+30筹码。',
  badge: {
    type: 'chip',
    text: '+30 Chips'
  },
  tags: ['增强类型']
}, {
  id: 'mult-card',
  english: 'Mult Card',
  chinese: '倍率牌',
  pinyin: 'bèi lǜ pái',
  category: 'special',
  explanation: '增强牌之一，红色背景。每次计分时额外给+4倍率。',
  badge: {
    type: 'mult',
    text: '+4 Mult'
  },
  tags: ['增强类型']
}, {
  id: 'wild-card',
  english: 'Wild Card',
  chinese: '万能牌',
  pinyin: 'wàn néng pái',
  category: 'special',
  explanation: '增强牌之一，可以视为任意花色。对于需要特定花色的牌型（如同花）非常有用。',
  badge: {
    type: 'purple',
    text: '任意花色'
  },
  tags: ['增强类型']
}, {
  id: 'glass-card',
  english: 'Glass Card',
  chinese: '玻璃牌',
  pinyin: 'bō li pái',
  category: 'special',
  explanation: '增强牌之一，蓝色透明效果。每次计分时×2倍率（非常强！），但每次使用有1/4概率破碎销毁。高风险高回报。',
  badge: {
    type: 'xmult',
    text: '×2 Mult ⚠️易碎'
  },
  tags: ['增强类型', '高风险']
}, {
  id: 'steel-card',
  english: 'Steel Card',
  chinese: '钢铁牌',
  pinyin: 'gāng tiě pái',
  category: 'special',
  explanation: '增强牌之一，灰色金属效果。只要这张牌留在手中（不需要打出），每轮给×1.5倍率。',
  badge: {
    type: 'xmult',
    text: '×1.5 Mult（留手中）'
  },
  tags: ['增强类型']
}, {
  id: 'stone-card',
  english: 'Stone Card',
  chinese: '石头牌',
  pinyin: 'shí tou pái',
  category: 'special',
  explanation: '增强牌之一，灰色石头效果。没有花色和点数，每次计分给+50筹码。无法参与任何牌型组合，但筹码贡献很高。',
  badge: {
    type: 'chip',
    text: '+50 Chips（无花色）'
  },
  tags: ['增强类型']
}, {
  id: 'gold-card',
  english: 'Gold Card',
  chinese: '金牌',
  pinyin: 'jīn pái',
  category: 'special',
  explanation: '增强牌之一，金色效果。打出这张牌时获得$3金币。',
  badge: {
    type: 'gold',
    text: '+$3 金币'
  },
  tags: ['增强类型']
}, {
  id: 'lucky-card',
  english: 'Lucky Card',
  chinese: '幸运牌',
  pinyin: 'xìng yùn pái',
  category: 'special',
  explanation: '增强牌之一，绿色四叶草效果。每次计分有1/5概率给+20倍率，有1/15概率给$20金币。纯靠运气！',
  badge: {
    type: 'mult',
    text: '1/5概率 +20 Mult'
  },
  tags: ['增强类型', '随机']
}, {
  id: 'gold-seal',
  english: 'Gold Seal',
  chinese: '金色印章',
  pinyin: 'jīn sè yìn zhāng',
  category: 'special',
  explanation: '印章之一，打出带有金色印章的牌时获得$3金币。',
  badge: {
    type: 'gold',
    text: '+$3'
  },
  tags: ['印章']
}, {
  id: 'red-seal',
  english: 'Red Seal',
  chinese: '红色印章',
  pinyin: 'hóng sè yìn zhāng',
  category: 'special',
  explanation: '印章之一，打出带有红色印章的牌时，该牌的效果重复触发一次（Retrigger）。',
  badge: {
    type: 'mult',
    text: '重复触发'
  },
  tags: ['印章']
}, {
  id: 'blue-seal',
  english: 'Blue Seal',
  chinese: '蓝色印章',
  pinyin: 'lán sè yìn zhāng',
  category: 'special',
  explanation: '印章之一，打出带有蓝色印章的牌时，获得对应该牌点数的星球牌（Planet Card）。',
  badge: {
    type: 'chip',
    text: '获得星球牌'
  },
  tags: ['印章']
}, {
  id: 'purple-seal',
  english: 'Purple Seal',
  chinese: '紫色印章',
  pinyin: 'zǐ sè yìn zhāng',
  category: 'special',
  explanation: '印章之一，弃掉带有紫色印章的牌时，获得对应该牌花色的塔罗牌（Tarot Card）。',
  badge: {
    type: 'purple',
    text: '弃牌获塔罗'
  },
  tags: ['印章']
},
// DECKS
{
  id: 'red-deck',
  english: 'Red Deck',
  chinese: '红色牌组',
  pinyin: 'hóng sè pái zǔ',
  category: 'decks',
  explanation: '起始牌组之一。每轮多1次弃牌机会。适合需要频繁换牌的策略。',
  badge: {
    type: 'mult',
    text: '+1 弃牌'
  },
  tags: ['牌组']
}, {
  id: 'blue-deck',
  english: 'Blue Deck',
  chinese: '蓝色牌组',
  pinyin: 'lán sè pái zǔ',
  category: 'decks',
  explanation: '起始牌组之一。每轮多1次出牌机会。适合需要多次出牌积累分数的策略。',
  badge: {
    type: 'chip',
    text: '+1 出牌次数'
  },
  tags: ['牌组']
}, {
  id: 'yellow-deck',
  english: 'Yellow Deck',
  chinese: '黄色牌组',
  pinyin: 'huáng sè pái zǔ',
  category: 'decks',
  explanation: '起始牌组之一。每局游戏开始时多$10金币。适合想要快速购买强力小丑牌的策略。',
  badge: {
    type: 'gold',
    text: '+$10 起始金币'
  },
  tags: ['牌组']
}, {
  id: 'green-deck',
  english: 'Green Deck',
  chinese: '绿色牌组',
  pinyin: 'lǜ sè pái zǔ',
  category: 'decks',
  explanation: '起始牌组之一。每次出牌获得$1，每次弃牌失去$1。鼓励少弃牌多出牌的策略。',
  badge: {
    type: 'green',
    text: '出牌+$1'
  },
  tags: ['牌组']
}, {
  id: 'black-deck',
  english: 'Black Deck',
  chinese: '黑色牌组',
  pinyin: 'hēi sè pái zǔ',
  category: 'decks',
  explanation: '起始牌组之一。多1个小丑槽位（可持有6张小丑牌），但每轮少1次出牌机会。',
  badge: {
    type: 'purple',
    text: '+1 小丑槽位'
  },
  tags: ['牌组']
}, {
  id: 'magic-deck',
  english: 'Magic Deck',
  chinese: '魔法牌组',
  pinyin: 'mó fǎ pái zǔ',
  category: 'decks',
  explanation: '起始牌组之一。开局获得The Fool塔罗牌和The High Priestess塔罗牌各一张。',
  tags: ['牌组']
}, {
  id: 'abandoned-deck',
  english: 'Abandoned Deck',
  chinese: '废弃牌组',
  pinyin: 'fèi qì pái zǔ',
  category: 'decks',
  explanation: '起始牌组之一。牌组中没有人头牌（J、Q、K），只有A和数字牌。共40张牌。',
  tags: ['牌组']
}, {
  id: 'ghost-deck',
  english: 'Ghost Deck',
  chinese: '幽灵牌组',
  pinyin: 'yōu líng pái zǔ',
  category: 'decks',
  explanation: '起始牌组之一。每次出牌有1/7概率获得一张幽灵牌（Spectral Card）。',
  tags: ['牌组']
}, {
  id: 'checkered-deck',
  english: 'Checkered Deck',
  chinese: '棋盘牌组',
  pinyin: 'qí pán pái zǔ',
  category: 'decks',
  explanation: '起始牌组之一。牌组中只有黑桃和红心（各26张），没有方块和梅花。',
  tags: ['牌组']
}, {
  id: 'plasma-deck',
  english: 'Plasma Deck',
  chinese: '等离子牌组',
  pinyin: 'děng lí zǐ pái zǔ',
  category: 'decks',
  explanation: '起始牌组之一。每次出牌后，筹码和倍率取平均值（(Chips+Mult)/2），然后都变成这个平均值。鼓励同时提升筹码和倍率。',
  badge: {
    type: 'xmult',
    text: '筹码倍率取平均'
  },
  tags: ['牌组', '特殊机制']
}];
const badgeStyles = {
  chip: {
    bg: 'rgba(79,195,247,0.15)',
    border: 'rgba(79,195,247,0.4)',
    color: '#4fc3f7'
  },
  mult: {
    bg: 'rgba(255,68,68,0.15)',
    border: 'rgba(255,68,68,0.4)',
    color: '#ff6666'
  },
  xmult: {
    bg: 'rgba(255,140,0,0.15)',
    border: 'rgba(255,140,0,0.4)',
    color: '#ff8c00'
  },
  gold: {
    bg: 'rgba(255,215,0,0.15)',
    border: 'rgba(255,215,0,0.4)',
    color: '#FFD700'
  },
  purple: {
    bg: 'rgba(180,79,255,0.15)',
    border: 'rgba(180,79,255,0.4)',
    color: '#b44fff'
  },
  green: {
    bg: 'rgba(80,200,80,0.15)',
    border: 'rgba(80,200,80,0.4)',
    color: '#60c060'
  },
  red: {
    bg: 'rgba(255,68,68,0.15)',
    border: 'rgba(255,68,68,0.4)',
    color: '#ff4444'
  }
};
export function GlossaryPage() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return terms.filter(term => {
      const matchCategory = activeCategory === 'all' || term.category === activeCategory;
      if (!matchCategory) return false;
      if (!q) return true;
      return term.english.toLowerCase().includes(q) || term.chinese.includes(q) || term.pinyin?.toLowerCase().includes(q) || term.explanation.includes(q) || term.tags?.some(t => t.includes(q));
    });
  }, [search, activeCategory]);
  return <div className="min-h-screen" style={{
    background: 'var(--bg-deep)'
  }} data-id="element-461">
      {/* Header */}
      <div style={{
      background: 'linear-gradient(180deg, #0d0618 0%, #1a0a2e 100%)',
      borderBottom: '1px solid rgba(255,215,0,0.2)',
      padding: '2rem 1rem 1.5rem'
    }} data-id="element-462">
        <div className="max-w-5xl mx-auto" data-id="element-463">
          <div className="flex items-center gap-3 mb-1" data-id="element-464">
            <span className="text-3xl" data-id="element-465">📖</span>
            <h1 className="text-3xl font-black glow-gold" style={{
            color: '#FFD700',
            fontFamily: 'Cinzel, serif'
          }} data-id="element-466">
              术语词典
            </h1>
          </div>
          <p className="text-sm mb-1" style={{
          color: '#b44fff',
          fontFamily: 'Cinzel, serif'
        }} data-id="element-467">
            Balatro English–Chinese Glossary
          </p>
          <p className="text-xs" style={{
          color: '#7060a0'
        }} data-id="element-468">
            游戏中看不懂的英文词？在这里查！ · Can't understand an English term
            in-game? Look it up here!
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6" data-id="element-469">
        {/* Search */}
        <div className="relative mb-5" data-id="element-470">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg" style={{
          color: '#7060a0'
        }} data-id="element-471">
            🔍
          </span>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索英文或中文词汇... (e.g. Blind, 倍率, Joker)" className="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none transition-all duration-200" style={{
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,215,0,0.2)',
          color: '#e8d5ff',
          fontFamily: 'Inter, sans-serif'
        }} onFocus={e => {
          e.currentTarget.style.border = '1px solid rgba(255,215,0,0.5)';
          e.currentTarget.style.boxShadow = '0 0 15px rgba(255,215,0,0.1)';
        }} onBlur={e => {
          e.currentTarget.style.border = '1px solid rgba(255,215,0,0.2)';
          e.currentTarget.style.boxShadow = 'none';
        }} data-id="element-472" />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{
          color: '#7060a0',
          cursor: 'pointer'
        }} data-id="element-473">
              ✕
            </button>}
        </div>

        {/* Category tabs */}
        <div className="flex gap-1.5 flex-wrap mb-6" data-id="element-474">
          {categories.map(cat => {
          const count = cat.id === 'all' ? terms.length : terms.filter(t => t.category === cat.id).length;
          return <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`nav-tab flex items-center gap-1 ${activeCategory === cat.id ? 'active' : ''}`} style={{
            fontSize: '0.7rem'
          }} data-id="element-475">
                <span data-id="element-476">{cat.emoji}</span>
                <span data-id="element-477">{cat.label}</span>
                <span className="number-mono" style={{
              fontSize: '0.65rem',
              color: activeCategory === cat.id ? 'rgba(255,215,0,0.7)' : '#5040a0',
              marginLeft: 2
            }} data-id="element-478">
                  {count}
                </span>
              </button>;
        })}
        </div>

        {/* Results count */}
        <div className="mb-4 flex items-center justify-between" data-id="element-479">
          <p className="text-xs" style={{
          color: '#7060a0'
        }} data-id="element-480">
            {search || activeCategory !== 'all' ? `找到 ${filtered.length} 个词条` : `共 ${terms.length} 个词条`}
          </p>
          {(search || activeCategory !== 'all') && <button onClick={() => {
          setSearch('');
          setActiveCategory('all');
        }} className="text-xs" style={{
          color: '#9080b0',
          cursor: 'pointer'
        }} data-id="element-481">
              清除筛选
            </button>}
        </div>

        {/* Terms grid */}
        {filtered.length === 0 ? <div className="text-center py-16" data-id="element-482">
            <div className="text-4xl mb-3" data-id="element-483">🔎</div>
            <p style={{
          color: '#7060a0'
        }} data-id="element-484">
              没有找到匹配的词条
            </p>
            <p className="text-xs mt-1" style={{
          color: '#5040a0'
        }} data-id="element-485">
              试试其他关键词
            </p>
          </div> : <div className="grid md:grid-cols-2 gap-3" data-id="element-486">
            {filtered.map(term => {
          const isExpanded = expandedId === term.id;
          return <button key={term.id} onClick={() => setExpandedId(isExpanded ? null : term.id)} className="panel text-left transition-all duration-200" style={{
            padding: '1rem',
            border: isExpanded ? '1px solid rgba(255,215,0,0.4)' : undefined,
            boxShadow: isExpanded ? '0 0 20px rgba(255,215,0,0.1)' : undefined,
            cursor: 'pointer',
            display: 'block',
            width: '100%'
          }} data-id="element-487">
                  {/* Term header */}
                  <div className="flex items-start justify-between gap-2 mb-2" data-id="element-488">
                    <div className="flex-1 min-w-0" data-id="element-489">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-490">
                        <span className="font-bold text-base" style={{
                    color: '#FFD700',
                    fontFamily: 'Cinzel, serif',
                    letterSpacing: '0.02em'
                  }} data-id="element-491">
                          {term.english}
                        </span>
                        {term.badge && <span className="text-xs px-2 py-0.5 rounded number-mono flex-shrink-0" style={{
                    background: badgeStyles[term.badge.type].bg,
                    border: `1px solid ${badgeStyles[term.badge.type].border}`,
                    color: badgeStyles[term.badge.type].color
                  }} data-id="element-492">
                            {term.badge.text}
                          </span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5" data-id="element-493">
                        <span className="font-bold text-sm" style={{
                    color: '#d0b0ff'
                  }} data-id="element-494">
                          {term.chinese}
                        </span>
                        {term.pinyin && <span className="text-xs" style={{
                    color: '#6050a0',
                    fontStyle: 'italic'
                  }} data-id="element-495">
                            {term.pinyin}
                          </span>}
                      </div>
                    </div>
                    <span className="text-xs flex-shrink-0 mt-1" style={{
                color: '#5040a0',
                transform: isExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s ease',
                display: 'inline-block'
              }} data-id="element-496">
                      ▼
                    </span>
                  </div>

                  {/* Preview (always visible) */}
                  <p className="text-xs leading-relaxed" style={{
              color: '#9080b0',
              display: '-webkit-box',
              WebkitLineClamp: isExpanded ? 'unset' : 2,
              WebkitBoxOrient: 'vertical',
              overflow: isExpanded ? 'visible' : 'hidden'
            }} data-id="element-497">
                    {term.explanation}
                  </p>

                  {/* Expanded content */}
                  {isExpanded && <div className="mt-3 space-y-2" data-id="element-498">
                      {term.example && <div className="p-2.5 rounded-lg text-xs" style={{
                background: 'rgba(255,215,0,0.05)',
                border: '1px solid rgba(255,215,0,0.15)',
                color: '#c0a060'
              }} data-id="element-499">
                          <span style={{
                  color: '#FFD700',
                  fontWeight: 600
                }} data-id="element-500">
                            💡 例子：
                          </span>{' '}
                          {term.example}
                        </div>}
                      {term.tags && term.tags.length > 0 && <div className="flex flex-wrap gap-1" data-id="element-501">
                          {term.tags.map(tag => <button key={tag} onClick={e => {
                  e.stopPropagation();
                  setSearch(tag);
                  setActiveCategory('all');
                }} className="text-xs px-2 py-0.5 rounded-full" style={{
                  background: 'rgba(180,79,255,0.1)',
                  border: '1px solid rgba(180,79,255,0.2)',
                  color: '#9060c0',
                  cursor: 'pointer'
                }} data-id="element-502">
                              #{tag}
                            </button>)}
                        </div>}
                    </div>}
                </button>;
        })}
          </div>}

        {/* Footer */}
        <div className="mt-10 text-center pb-8" data-id="element-503">
          <div className="section-divider mb-4" data-id="element-504" />
          <p className="text-xs" style={{
          color: '#4a3060'
        }} data-id="element-505">
            Balatro 中英词典 · 非官方 · 持续更新中
          </p>
        </div>
      </div>
    </div>;
}